import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { EmployeeRoleService } from '../services/employee-role.service';
import * as EmployeeRoleActions from './employee-roles.actions';

@Injectable()
export class EmployeeRolesEffects {
    private _actions$ = inject(Actions);
    private _employeeRoleService = inject(EmployeeRoleService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all employeeroles',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAllEmployeeRoles$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeRoleActions.loadEmployeeRoles),
            switchMap(() => {
                return this._employeeRoleService.getAll().pipe(
                    map((res) => {
                        return EmployeeRoleActions.loadEmployeeRolesSuccess({
                            roles: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeRoleActions.loadEmployeeRolesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One
    getEmployeeRole$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeRoleActions.loadEmployeeRole),
            switchMap((action) => {
                return this._employeeRoleService.getOne(action.id).pipe(
                    map((res) => {
                        return EmployeeRoleActions.loadEmployeeRoleSuccess({
                            role: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeRoleActions.loadEmployeeRoleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create
    createEmployeeRole$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeRoleActions.createEmployeeRole),
            switchMap((action) => {
                return this._employeeRoleService.create(action.data).pipe(
                    map((res) => {
                        return EmployeeRoleActions.createEmployeeRoleSuccess({
                            role: res.data,
                            success: 'Employee Role created successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeRoleActions.createEmployeeRoleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update EmployeeRole
    updateEmployeeRole$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeRoleActions.updateEmployeeRole),
            switchMap((action) => {
                return this._employeeRoleService
                    .update(action.data, action.id)
                    .pipe(
                        map((res) => {
                            return EmployeeRoleActions.updateEmployeeRoleSuccess(
                                {
                                    role: res.data,
                                    success:
                                        'Employee Role updated successfully',
                                }
                            );
                        }),
                        catchError((error) =>
                            of(
                                EmployeeRoleActions.updateEmployeeRoleFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete EmployeeRole
    deleteEmployeeRole$ = createEffect(() =>
        this._actions$.pipe(
            ofType(EmployeeRoleActions.deleteEmployeeRole),
            switchMap((action) => {
                return this._employeeRoleService.delete(action.id).pipe(
                    map(() => {
                        return EmployeeRoleActions.deleteEmployeeRoleSuccess({
                            id: action.id,
                            success: 'Employee Role deleted successfully',
                        });
                    }),
                    catchError((error) =>
                        of(
                            EmployeeRoleActions.deleteEmployeeRoleFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onEmployeeRoleActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    EmployeeRoleActions.createEmployeeRoleSuccess,
                    EmployeeRoleActions.updateEmployeeRoleSuccess,
                    EmployeeRoleActions.deleteEmployeeRoleSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onEmployeeRoleActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    EmployeeRoleActions.loadEmployeeRolesFailure,
                    EmployeeRoleActions.loadEmployeeRoleFailure,
                    EmployeeRoleActions.createEmployeeRoleFailure,
                    EmployeeRoleActions.updateEmployeeRoleFailure,
                    EmployeeRoleActions.deleteEmployeeRoleFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
